import {
	OrderQuoteVetterJobStatus,
	useSetOrderQuoteVetterJobItemsMutation,
	useWithdrawOrderQuoteVetterJobMutation,
	VetterJobStage,
	type GetOrderQuoteVetterJobsQuery,
} from '@codezo/graphql';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatDate } from '../../../lib/utils';
import type { StatusTone } from '../../../lib/status-configs';
import { Button } from '../../ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { StatusBadge } from '../../ui/status-badge';
import { QuoteLinePicker, type QuoteLine } from './QuoteLinePicker';
import { VetterJobLinks } from './VetterJobLinks';
import { VetterJobPrices } from './VetterJobPrices';

type VetterJob = GetOrderQuoteVetterJobsQuery['getOrderQuoteVetterJobs'][number];

/** The badge's tone per stage. The words are the backend's `stageLabel`. */
const STAGE_TONE: Record<VetterJobStage, StatusTone> = {
	[VetterJobStage.AwaitingPrices]: 'warn',
	[VetterJobStage.PricesSubmitted]: 'success',
	[VetterJobStage.InProduction]: 'info',
	[VetterJobStage.ToShip]: 'info',
	[VetterJobStage.Shipped]: 'success',
	[VetterJobStage.Closed]: 'neutral',
};

/**
 * One supplier's request on this quote: products, stage, what they sent back,
 * and who was reached by link.
 */
export function VetterJobRow({
	job,
	lines,
	editable,
	onChanged,
	onUsePrices,
}: {
	job: VetterJob;
	lines: QuoteLine[];
	editable: boolean;
	onChanged: () => void;
	/** Open the "use supplier prices" picker on this supplier. */
	onUsePrices: (orderQuoteItemId: string, productName: string) => void;
}) {
	const [mode, setMode] = useState<'view' | 'lines' | 'withdraw'>('view');
	const [draftIds, setDraftIds] = useState<string[]>(job.orderQuoteItemIds);

	const [setItems, { loading: savingLines }] = useSetOrderQuoteVetterJobItemsMutation();
	const [withdraw, { loading: withdrawing }] = useWithdrawOrderQuoteVetterJobMutation();

	const isActive = job.status === OrderQuoteVetterJobStatus.Active;
	const lineName = (id: string) => lines.find((l) => l.id === id)?.name;
	const lineNames = job.orderQuoteItemIds.map(lineName).filter((n): n is string => !!n);
	// Asked for but not answered — worth saying, since there's nothing to use for them.
	const unpricedNames = job.submittedAt
		? job.orderQuoteItemIds
				.filter((id) => !job.prices.some((p) => p.orderQuoteItemId === id))
				.map(lineName)
				.filter((n): n is string => !!n)
		: [];

	// Withdrawn requests can't be applied to an offer, so only active ones offer it.
	const canUse = editable && isActive && job.prices.length > 0;

	const handleSaveLines = async () => {
		try {
			const { data } = await setItems({
				variables: { id: job.id, orderQuoteItemIds: draftIds },
			});
			const res = data?.setOrderQuoteVetterJobItems;
			if (!res?.success) throw new Error(res?.message || 'Could not update the products');
			toast.success('Products updated');
			setMode('view');
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not update the products');
		}
	};

	const handleWithdraw = async () => {
		try {
			const { data } = await withdraw({ variables: { id: job.id } });
			const res = data?.withdrawOrderQuoteVetterJob;
			if (!res?.success) throw new Error(res?.message || 'Could not withdraw the request');
			toast.success('Request withdrawn');
			setMode('view');
			onChanged();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Could not withdraw the request');
		}
	};

	return (
		<div className={isActive ? 'px-4 py-3' : 'px-4 py-3 opacity-60'}>
			<div className="flex items-start gap-3">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<span className="truncate text-sm font-medium text-text-1">
							{job.vetterEntity?.name ?? 'Supplier'}
						</span>
						{isActive ? (
							<StatusBadge
								active
								activeLabel={job.stageLabel}
								activeTone={STAGE_TONE[job.stage]}
							/>
						) : (
							<StatusBadge active activeLabel="Withdrawn" activeTone="neutral" />
						)}
					</div>
					<div className="mt-0.5 truncate text-xs text-text-3">
						{lineNames.length > 0 ? lineNames.join(', ') : 'No products'}
					</div>
					<div className="mt-0.5 text-xs text-text-4">
						{job.createdAt ? `Requested ${formatDate(job.createdAt)}` : 'Requested'}
						{job.submittedAt && ` · answered ${formatDate(job.submittedAt)}`}
						{job.withdrawnAt && ` · withdrawn ${formatDate(job.withdrawnAt)}`}
					</div>
					{unpricedNames.length > 0 && (
						<div className="mt-0.5 text-xs text-status-warn">
							Not priced: {unpricedNames.join(', ')}
						</div>
					)}
				</div>

				{isActive && editable && mode === 'view' && (
					<DropdownMenu>
						<DropdownMenuTrigger className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
							<MoreHorizontal size={14} />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onClick={() => {
									setDraftIds(job.orderQuoteItemIds);
									setMode('lines');
								}}
							>
								Change products
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem onClick={() => setMode('withdraw')}>
								Withdraw request
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>

			{mode === 'lines' && (
				<div className="mt-3 flex flex-col gap-2">
					<QuoteLinePicker
						lines={lines}
						value={draftIds}
						onChange={setDraftIds}
						disabled={savingLines}
					/>
					<div className="flex justify-end gap-1">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setMode('view')}
							disabled={savingLines}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							onClick={handleSaveLines}
							disabled={savingLines || draftIds.length === 0}
						>
							{savingLines ? 'Saving…' : 'Save'}
						</Button>
					</div>
				</div>
			)}

			{mode === 'withdraw' && (
				<div className="mt-3 flex flex-col gap-2 border-t border-line-1 pt-2">
					<div className="text-sm text-text-3">
						Withdraw this request? It leaves {job.vetterEntity?.name ?? 'the supplier'}
						&apos;s list and every link into it stops working. Prices they already sent
						stay usable here.
					</div>
					<div className="flex items-center gap-1">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setMode('view')}
							disabled={withdrawing}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							size="sm"
							onClick={handleWithdraw}
							disabled={withdrawing}
						>
							{withdrawing ? 'Withdrawing…' : 'Withdraw'}
						</Button>
					</div>
				</div>
			)}

			{/* Shown outright — what the supplier sent is the reason the row exists. */}
			{job.prices.length > 0 && (
				<div className="mt-3">
					<VetterJobPrices
						job={job}
						lines={lines}
						onUse={canUse ? onUsePrices : undefined}
					/>
				</div>
			)}

			{isActive && (
				<div className="mt-3">
					<VetterJobLinks links={job.links} editable={editable} onChanged={onChanged} />
				</div>
			)}
		</div>
	);
}
